import React from 'react'
import { Grid, Link } from '@mui/material'
import View from 'views/View'
import { withRequiredRole } from 'components/useRequireRole'
import EntFeature from 'models/EntFeature'
import { AccentArea } from 'components'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import {
    CLIENT_THEME_VARIANT,
    useThemeStyling,
} from 'hooks/useThemeStyling/useThemeStyling'
import TopicArea from './Topic'
import { useHelp } from './Help.vm'
import idDirectory from './idAttributes'

/**
 * The Help view with it's videos
 */

export const Help = (): React.ReactElement => {
    const help = useHelp()
    const { USER_DOC, ADD_ONS } = useFeatureToggle('HELP')

    const { themeVariantObj } = useThemeStyling()
    const isSabreVariant =
        themeVariantObj.id === CLIENT_THEME_VARIANT.SABRE.id ||
        themeVariantObj.id === CLIENT_THEME_VARIANT.LASTMINUTE.id

    const isSofiVariant = themeVariantObj.id === CLIENT_THEME_VARIANT.SOFI.id

    const sabreTopics = isSabreVariant
        ? [
              {
                  id: 'sabre-2',
                  title: 'Case Builder',
                  description:
                      'This video provides a step-by-step walkthrough of creating a dispute using Case Builder, guiding you through each stage from entering case details to submitting the dispute. You’ll also learn how to attach supporting documentation, select the appropriate dispute reason, and review your submission to ensure accuracy before finalizing.',
                  category: 'Case Management',
                  testId: 'case-builder-in-depth',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-case-builder-in-depth.png',
              },
              {
                  id: 'sabre-3',
                  title: 'Case Management',
                  description:
                      'This video provides a comprehensive overview of Case Management and its key features, serving as the central hub for viewing and managing all cases. You will learn how to track case statuses, apply filters and sorting options, and quickly access detailed case information.',
                  category: 'Case Management',
                  testId: 'case-management',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-case-management-overview.png',
              },
              {
                  id: 'sabre-4',
                  title: 'Assigning a Case',
                  description:
                      'This video demonstrates how to assign a case to a specific user for follow-up and resolution. You will learn how to access case details, select an assignee from the available user list, and confirm the assignment. The demonstration also covers how to reassign cases when needed and track ownership to ensure accountability throughout the case lifecycle.',
                  category: 'Case Management',
                  testId: 'assigning-a-case',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-assigning-a-case.png',
              },
              {
                  id: 'sabre-5',
                  title: 'Case Review',
                  description:
                      'This video demonstrates how an agent can review, resolve, or continue the dispute from start to finish. You will learn how to assess case details, evaluate supporting documentation, and determine the appropriate resolution. The demonstration also covers the available options to route the case to another agent for further review.',
                  category: 'Case Management',
                  testId: 'case-review',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-case-review.png',
              },
              {
                  id: 'sabre-6',
                  title: 'Case Activity',
                  description:
                      'This video provides an overview of Case Activity and its key features, offering a centralized view of all cases related to a specific dispute. You will learn how to review case verdicts, navigate through related case records, and export case data for reporting and analysis.',
                  category: 'Case Activity',
                  testId: 'case-activity',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-case-activity.png',
              },
              {
                  id: 'sabre-7',
                  title: 'User Management',
                  description:
                      'This video provides an overview of User Management and its key features, enabling administrators to effectively manage platform access. You will learn how to create new users, adjust account permissions, lock or unlock user accounts, and reset passwords to maintain security and control.',
                  category: 'Administration',
                  testId: 'user-management',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-user-management.png',
              },
              {
                  id: 'sabre-8',
                  title: 'Webhooks',
                  description:
                      'This video provides an overview of Webhooks and their key features. You will learn how to create webhook subscriptions for case updates, customize messages within callback payloads, and enable efficient, automated communication between systems.',
                  category: 'Webhooks',
                  testId: 'webhooks',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-webhooks.png',
              },
              {
                  id: 'sabre-9',
                  title: 'Profile Settings',
                  description:
                      'This video provides an overview of Profile Settings and their key features, enabling users to manage their account information and preferences. You will learn how to update your email address, change your password, and configure notification preferences. The video also covers viewing your login history and using available tools to maintain account security.',
                  category: 'Administration',
                  testId: 'profile-settings',
                  videoUrl: 'sabre-case-builder-create-chargeback.mp4',
                  videoType: 'video/mp4',
                  thumbnailUrl: 'sabre-profile-settings.png',
              },
          ]
        : isSofiVariant
        ? [
              {
                  id: 'sabre-2',
                  title: 'Case Builder',
                  description:
                      'This video provides a step-by-step walkthrough of creating a dispute using Case Builder, guiding you through each stage from entering case details to submitting the dispute. You’ll also learn how to attach supporting documentation, select the appropriate dispute reason, and review your submission to ensure accuracy before finalizing.',
                  category: 'Case Management',
                  testId: 'case-builder-in-depth',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-3',
                  title: 'Case Management',
                  description:
                      'This video provides a comprehensive overview of Case Management and its key features, serving as the central hub for viewing and managing all cases. You will learn how to track case statuses, apply filters and sorting options, and quickly access detailed case information.',
                  category: 'Case Management',
                  testId: 'case-management',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-4',
                  title: 'Assigning a Case',
                  description:
                      'This video demonstrates how to assign a case to a specific user for follow-up and resolution. You will learn how to access case details, select an assignee from the available user list, and confirm the assignment. The demonstration also covers how to reassign cases when needed and track ownership to ensure accountability throughout the case lifecycle.',
                  category: 'Case Management',
                  testId: 'assigning-a-case',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-5',
                  title: 'Case Review',
                  description:
                      'This video demonstrates how an agent can review, resolve, or continue the dispute from start to finish. You will learn how to assess case details, evaluate supporting documentation, and determine the appropriate resolution. The demonstration also covers the available options to route the case to another agent for further review.',
                  category: 'Case Management',
                  testId: 'case-review',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-6',
                  title: 'Case Activity',
                  description:
                      'This video provides an overview of Case Activity and its key features, offering a centralized view of all cases related to a specific dispute. You will learn how to review case verdicts, navigate through related case records, and export case data for reporting and analysis.',
                  category: 'Case Activity',
                  testId: 'case-activity',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-7',
                  title: 'User Management',
                  description:
                      'This video provides an overview of User Management and its key features, enabling administrators to effectively manage platform access. You will learn how to create new users, adjust account permissions, lock or unlock user accounts, and reset passwords to maintain security and control.',
                  category: 'Administration',
                  testId: 'user-management',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-8',
                  title: 'Webhooks',
                  description:
                      'This video provides an overview of Webhooks and their key features. You will learn how to create webhook subscriptions for case updates, customize messages within callback payloads, and enable efficient, automated communication between systems.',
                  category: 'Webhooks',
                  testId: 'webhooks',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
              {
                  id: 'sabre-9',
                  title: 'Profile Settings',
                  description:
                      'This video provides an overview of Profile Settings and their key features, enabling users to manage their account information and preferences. You will learn how to update your email address, change your password, and configure notification preferences. The video also covers viewing your login history and using available tools to maintain account security.',
                  category: 'Administration',
                  testId: 'profile-settings',
                  videoUrl: 'user_management_unbranded.mp4',
                  videoType: 'video/mp4',
              },
          ]
        : help.topics

    return (
        <View title="Help Center" breadcrumb="Help Center" testId="help">
            {USER_DOC.enabled && !isSabreVariant && !isSofiVariant && (
                <Grid
                    container
                    className="emp-documents"
                    id={`${idDirectory.gridContainer}-documents`}
                >
                    <Grid item xs={12} id={`${idDirectory.gridItem}-documents`}>
                        <div className="c-help-center-title">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                fill="none"
                            >
                                <path
                                    d="M3 15.125V3.875C3 3.37772 3.19754 2.90081 3.54917 2.54917C3.90081 2.19754 4.37772 2 4.875 2H14.25C14.4489 2 14.6397 2.07902 14.7803 2.21967C14.921 2.36032 15 2.55109 15 2.75V16.25C15 16.4489 14.921 16.6397 14.7803 16.7803C14.6397 16.921 14.4489 17 14.25 17H4.875C4.37772 17 3.90081 16.8025 3.54917 16.4508C3.19754 16.0992 3 15.6223 3 15.125ZM3 15.125C3 14.6277 3.19754 14.1508 3.54917 13.7992C3.90081 13.4475 4.37772 13.25 4.875 13.25H15"
                                    stroke="#030213"
                                    strokeWidth="1.66667"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Documentation
                        </div>
                        <AccentArea testId="help">
                            <Link
                                href={
                                    USER_DOC.user_guide_link_is_local_file
                                        ? `${process.env.PUBLIC_URL}/help/${USER_DOC.user_guide_link}`
                                        : USER_DOC.user_guide_link
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="c-help-center-documentation-link"
                            >
                                <div className="c-help-center-documentation-link__icon">
                                    <svg
                                        width="21"
                                        height="21"
                                        viewBox="0 0 21 21"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13 2.16666H5.49998C5.05795 2.16666 4.63403 2.34225 4.32147 2.65481C4.00891 2.96737 3.83331 3.3913 3.83331 3.83332V17.1667C3.83331 17.6087 4.00891 18.0326 4.32147 18.3452C4.63403 18.6577 5.05795 18.8333 5.49998 18.8333H15.5C15.942 18.8333 16.3659 18.6577 16.6785 18.3452C16.9911 18.0326 17.1666 17.6087 17.1666 17.1667V6.33332L13 2.16666Z"
                                            stroke="var(--theme-primary)"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M12.1667 2.16666V5.49999C12.1667 5.94202 12.3423 6.36594 12.6548 6.6785C12.9674 6.99106 13.3913 7.16666 13.8334 7.16666H17.1667"
                                            stroke="var(--theme-primary)"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8.83335 8H7.16669"
                                            stroke="var(--theme-primary)"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M13.8334 11.3333H7.16669"
                                            stroke="var(--theme-primary)"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M13.8334 14.6667H7.16669"
                                            stroke="var(--theme-primary)"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="c-help-center-documentation-link__content">
                                    <div className="c-help-center-documentation-link__title">
                                        Dispute Portal User Guide
                                    </div>
                                    <div className="c-help-center-documentation-link__description">
                                        Comprehensive guide covering all portal
                                        features and workflows
                                    </div>
                                </div>
                                <div className="c-help-center-documentation-link__arrow">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="17"
                                        height="17"
                                        viewBox="0 0 17 17"
                                        fill="none"
                                    >
                                        <path
                                            d="M10.5 2.5H14.5V6.5"
                                            stroke="#717182"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M7.16666 9.83333L14.5 2.5"
                                            stroke="#717182"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M12.5 9.16667V13.1667C12.5 13.5203 12.3595 13.8594 12.1095 14.1095C11.8594 14.3595 11.5203 14.5 11.1667 14.5H3.83333C3.47971 14.5 3.14057 14.3595 2.89052 14.1095C2.64048 13.8594 2.5 13.5203 2.5 13.1667V5.83333C2.5 5.47971 2.64048 5.14057 2.89052 4.89052C3.14057 4.64048 3.47971 4.5 3.83333 4.5H7.83333"
                                            stroke="#717182"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </Link>
                        </AccentArea>
                    </Grid>
                </Grid>
            )}

            <div className="c-help-center-title">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                >
                    <path
                        d="M12 10.3692L15.9172 12.9807C15.9737 13.0183 16.0393 13.0398 16.1071 13.0431C16.1748 13.0463 16.2422 13.0311 16.302 12.9991C16.3618 12.9671 16.4118 12.9194 16.4467 12.8613C16.4815 12.8031 16.5 12.7365 16.5 12.6687V6.52169C16.5 6.4557 16.4826 6.39088 16.4496 6.33377C16.4165 6.27666 16.369 6.22928 16.3118 6.19641C16.2546 6.16354 16.1897 6.14635 16.1237 6.14658C16.0577 6.1468 15.993 6.16443 15.936 6.19769L12 8.49419"
                        stroke="#030213"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M10.5 5.1192H3C2.17157 5.1192 1.5 5.79077 1.5 6.6192V12.6192C1.5 13.4476 2.17157 14.1192 3 14.1192H10.5C11.3284 14.1192 12 13.4476 12 12.6192V6.6192C12 5.79077 11.3284 5.1192 10.5 5.1192Z"
                        stroke="#030213"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                Video Library
            </div>

            <Grid
                container
                spacing={3}
                id={`${idDirectory.gridContainer}-topics`}
                className="c-help-center-topics"
            >
                {sabreTopics.map((topic) =>
                    isSabreVariant ||
                    isSofiVariant ||
                    ADD_ONS.topicTitles.includes(topic.title) ? (
                        <Grid
                            item
                            key={topic.id}
                            xl={3}
                            lg={3}
                            md={6}
                            sm={12}
                            xs={12}
                            id={`${idDirectory.gridItem}-${topic.testId}`}
                            className="c-help-center-topics__item"
                        >
                            <TopicArea
                                topic={topic}
                                loading={false}
                                disabledMessage={
                                    isSabreVariant
                                        ? topic.title === 'Case Builder'
                                            ? undefined // show case builder video
                                            : 'Disabled in Demo' // disable all others
                                        : isSofiVariant
                                        ? 'Disabled in Demo' // disable all
                                        : undefined
                                }
                            />
                        </Grid>
                    ) : null
                )}
            </Grid>
        </View>
    )
}

export default withRequiredRole(EntFeature.HELP, Help)
